import { Stack, type SxProps, type Theme } from "@mui/material";
import {
  GRID_ROOT_GROUP_ID,
  gridExpandedSortedRowIdsSelector,
  gridRowNodeSelector,
  type GridApiPremium,
  type GridPaginationModel,
  type GridRenderCellParams,
  type GridRowId,
  type GridRowParams,
  type GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { use, useCallback, useMemo, useRef, useState, type RefObject } from "react";
import DataLoadingFailed from "../../../components/DataLoadingFailed.tsx";
import DataGrid from "../../../components/grid/DataGrid.tsx";
import useMergedSx from "../../../hooks/useMergedSx.ts";
import { logError } from "../../../logging.ts";
import { useGridDataSourceWithRangePaging, type FetchRowsPayload } from "./useGridDataSourceWithRangePaging.ts";
import GridSelectionContext from "./GridSelectionContext.ts";
import SelectionLimitContext from "./SelectionLimitContext.ts";
import { busySx, gridCheckboxColDef, PAGE_SIZE, singleSelectGridCheckboxColDef } from "./filterGridHelper.tsx";
import { getLimitedRangeSelection } from "./limitedRangeSelection.ts";
import SelectedItemsCount from "./SelectedItemsCount.tsx";
import {
  getSelectionUpToLimit,
  isSelectAllChecked,
  isSelectionWithinLimit,
  wouldExceedSelectionLimit,
} from "./selectionLimit.ts";
import type { TextFilterGridProps } from "./textFilterGridTypes.ts";

const DEFAULT_CONTAINER_MIN_HEIGHT = "255px";
const emptyRow = {};
const paginationModel: GridPaginationModel = { page: 0, pageSize: PAGE_SIZE };
const MAX_SKELETON_ROW_COUNT = 10_000;
const ROW_NUMBER_FIELD = "rowNumber";

const getDataGridRowCount = (rowCount: number | undefined): number | undefined =>
  rowCount !== undefined && rowCount > MAX_SKELETON_ROW_COUNT ? -1 : rowCount;

const getResultRowIds = (rows: Record<string, unknown>[]): GridRowId[] => {
  const rowIds: GridRowId[] = [];
  for (const row of rows) {
    const rowId = row["id"];
    if (typeof rowId === "string" || typeof rowId === "number") {
      rowIds.push(rowId);
    }
  }
  return rowIds;
};

const getSkeletonRowIds = (apiRef: RefObject<GridApiPremium | null>, rowIds: Iterable<GridRowId>): GridRowId[] => {
  const skeletonRowIds: GridRowId[] = [];
  for (const rowId of rowIds) {
    if (gridRowNodeSelector(apiRef, rowId)?.type === "skeletonRow") {
      skeletonRowIds.push(rowId);
    }
  }
  return skeletonRowIds;
};

const getLoadedRowIds = (apiRef: RefObject<GridApiPremium | null>): GridRowId[] =>
  gridExpandedSortedRowIdsSelector(apiRef).filter(
    (rowId) => gridRowNodeSelector(apiRef, rowId)?.type !== "skeletonRow"
  );

const isSameSelection = (ids: ReadonlySet<GridRowId>, other: ReadonlySet<GridRowId>): boolean =>
  ids.size === other.size && [...ids].every((id) => other.has(id));

const getLoadedRowsByNumber = (gridApi: GridApiPremium, start: number, end: number): Map<number, GridRowId> => {
  const rowsByNumber = new Map<number, GridRowId>();
  for (const [rowId, row] of gridApi.getRowModels()) {
    const rowNumber = row[ROW_NUMBER_FIELD];
    if (typeof rowNumber === "number" && rowNumber >= start && rowNumber <= end) {
      rowsByNumber.set(rowNumber, rowId);
    }
  }
  return rowsByNumber;
};

const TextFilterServerGrid = (props: TextFilterGridProps) => {
  const maxSelectedValues = use(SelectionLimitContext);
  const {
    initialSorting,
    columns,
    loadRows,
    queryKey,
    selectedRowIds,
    readonly,
    singleSelect,
    containerMinHeight,
    sx,
    onChanged,
  } = props;
  const [dataSourceError, setDataSourceError] = useState<Error>();
  const [actualTotalRows, setActualTotalRows] = useState<number>();
  const [resultRowIds, setResultRowIds] = useState<GridRowId[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => ({
    type: "include",
    ids: new Set(selectedRowIds),
  }));
  const selectionAnchorRef = useRef<GridRowId | undefined>(undefined);
  const selectedRowIdSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);
  const initialState = useMemo(() => ({ sorting: { sortModel: initialSorting } }), [initialSorting]);
  const gridContainerSx = useMemo(
    (): SxProps<Theme> => ({ minHeight: containerMinHeight ?? DEFAULT_CONTAINER_MIN_HEIGHT }),
    [containerMinHeight]
  );
  // The prop wins over the grid's own model, so a selection pruned outside the grid stops showing as checked.
  const effectiveSelectionModel = useMemo(
    (): GridRowSelectionModel =>
      isSameSelection(selectionModel.ids, selectedRowIdSet)
        ? selectionModel
        : { type: "include", ids: new Set(selectedRowIdSet) },
    [selectedRowIdSet, selectionModel]
  );

  const fetchRows = useCallback(
    async ({ range, sortModel }: FetchRowsPayload<{ queryKey: string }>) => {
      const result = await loadRows({
        range: {
          skip: range.skip ?? 0,
          take: range.take ?? PAGE_SIZE,
          totals: range.totals === true,
        },
        sortModel,
      });
      if (result.rowCount !== undefined) {
        setActualTotalRows(result.rowCount);
        setResultRowIds(range.skip === 0 && result.rows.length === result.rowCount ? getResultRowIds(result.rows) : []);
      }
      return {
        success: true as const,
        data: { items: result.rows, total: getDataGridRowCount(result.rowCount), grandTotalItem: emptyRow },
      };
    },
    [loadRows]
  );

  const { gridApiRef, dataSource } = useGridDataSourceWithRangePaging(fetchRows, { queryKey });

  const restoreSelection = useCallback(() => {
    setSelectionModel({ type: "include", ids: new Set(selectedRowIds) });
  }, [selectedRowIds]);

  const commitSelection = useCallback(
    (ids: Iterable<GridRowId>) => {
      const unique = [...new Set(ids)];
      if (!isSelectionWithinLimit(unique, maxSelectedValues)) {
        restoreSelection();
        return;
      }
      setSelectionModel({ type: "include", ids: new Set(unique) });
      onChanged(unique);
    },
    [maxSelectedValues, onChanged, restoreSelection]
  );

  const completeLimitedRangeSelection = useCallback(
    async (model: GridRowSelectionModel, skeletonRowIds: GridRowId[]) => {
      const gridApi = gridApiRef.current;
      const anchorId = selectionAnchorRef.current;
      if (gridApi === null || anchorId === undefined) {
        restoreSelection();
        return;
      }

      const rowIds = gridExpandedSortedRowIdsSelector(gridApiRef);
      // Limit the Shift-selection to the remaining capacity and identify the single missing page it needs.
      const range = getLimitedRangeSelection(
        rowIds,
        model.ids,
        selectedRowIds,
        skeletonRowIds,
        anchorId,
        maxSelectedValues
      );
      if (range === undefined) {
        restoreSelection();
        return;
      }

      // Keep rows already in memory and fetch only the adjacent page needed to complete the range.
      const loadedRowsByNumber = getLoadedRowsByNumber(gridApi, range.selectionStart, range.selectionEnd);
      restoreSelection();
      if (range.fetchRange !== undefined) {
        await gridApi.dataSource.fetchRows(GRID_ROOT_GROUP_ID, range.fetchRange);
      }

      for (const [rowNumber, rowId] of getLoadedRowsByNumber(gridApi, range.selectionStart, range.selectionEnd)) {
        loadedRowsByNumber.set(rowNumber, rowId);
      }

      // Commit only a complete range; never leave the user with an arbitrary partial Shift-selection.
      const nextSelection = new Set(selectedRowIds);
      for (let rowNumber = range.selectionStart; rowNumber <= range.selectionEnd; rowNumber += 1) {
        const rowId = loadedRowsByNumber.get(rowNumber);
        if (rowId === undefined) {
          restoreSelection();
          return;
        }
        nextSelection.add(rowId);
      }

      selectionAnchorRef.current = loadedRowsByNumber.get(range.scrollIndex);
      commitSelection(nextSelection);
      gridApi.scrollToIndexes({ rowIndex: range.scrollIndex });
    },
    [commitSelection, gridApiRef, maxSelectedValues, restoreSelection, selectedRowIds]
  );

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      if (model.type === "exclude" || readonly === true) {
        return;
      }

      const skeletonRowIds = getSkeletonRowIds(gridApiRef, model.ids);
      if (skeletonRowIds.length > 0 || !isSelectionWithinLimit(model.ids, maxSelectedValues)) {
        void completeLimitedRangeSelection(model, skeletonRowIds);
        return;
      }

      const addedRowIds = [...model.ids].filter((rowId) => !effectiveSelectionModel.ids.has(rowId));
      if (addedRowIds.length === 1) {
        selectionAnchorRef.current = addedRowIds[0];
      } else if (selectionAnchorRef.current !== undefined && !model.ids.has(selectionAnchorRef.current)) {
        selectionAnchorRef.current = undefined;
      }
      commitSelection(model.ids);
    },
    [
      commitSelection,
      completeLimitedRangeSelection,
      effectiveSelectionModel.ids,
      gridApiRef,
      maxSelectedValues,
      readonly,
    ]
  );

  const isRowIdSelectable = useCallback(
    (rowId: GridRowId) => !wouldExceedSelectionLimit(selectedRowIdSet, [rowId], maxSelectedValues),
    [maxSelectedValues, selectedRowIdSet]
  );
  const isRowSelectable = useCallback((params: GridRowParams) => isRowIdSelectable(params.id), [isRowIdSelectable]);
  const isCellSelectionDisabled = useCallback(
    (params: GridRenderCellParams) => !isRowIdSelectable(params.id),
    [isRowIdSelectable]
  );

  const handleDataSourceError = useCallback((error: Error) => {
    logError(error, "[TextFilterServerGrid]");
    setDataSourceError(error);
  }, []);

  const gridSx = useMergedSx(sx, busySx);
  const selectAllChecked = isSelectAllChecked(selectedRowIdSet, resultRowIds, maxSelectedValues);
  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      commitSelection(
        checked ? getSelectionUpToLimit(selectedRowIds, getLoadedRowIds(gridApiRef), maxSelectedValues) : []
      );
    },
    [commitSelection, gridApiRef, maxSelectedValues, selectedRowIds]
  );
  const gridSelectionState = useMemo(
    () => ({
      checked: selectAllChecked,
      indeterminate: actualTotalRows !== undefined && selectedRowIdSet.size > 0 && !selectAllChecked,
      onChange: handleSelectAllChange,
      isRowSelectionDisabled: isCellSelectionDisabled,
    }),
    [actualTotalRows, handleSelectAllChange, isCellSelectionDisabled, selectAllChecked, selectedRowIdSet.size]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading values failed" />;
  }

  return (
    <Stack spacing={0.5} sx={{ flex: 1 }}>
      <GridSelectionContext value={gridSelectionState}>
        <DataGrid
          containerSx={gridContainerSx}
          apiRef={gridApiRef}
          dataSource={dataSource}
          dataSourceCache={null}
          lazyLoading
          initialState={initialState}
          sx={gridSx}
          columns={columns}
          rowHeight={24}
          columnHeaderHeight={30}
          checkboxSelection={!readonly}
          checkboxColDef={singleSelect === true ? singleSelectGridCheckboxColDef : gridCheckboxColDef}
          disableMultipleRowSelection={singleSelect}
          isRowSelectable={isRowSelectable}
          keepNonExistentRowsSelected
          disableRowSelectionOnClick={readonly}
          rowSelectionModel={effectiveSelectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          sortingMode="server"
          onDataSourceError={handleDataSourceError}
          paginationModel={paginationModel}
          disableRowSelectionExcludeModel
        />
      </GridSelectionContext>
      <SelectedItemsCount
        selectedCount={selectedRowIdSet.size}
        totalCount={actualTotalRows}
        limit={maxSelectedValues}
      />
    </Stack>
  );
};

export default TextFilterServerGrid;
